import {z} from 'zod';
import {MovieStrokeType, AiAnimatorRangeBase} from 'common/src/modules/movie/movie.enums';
import {AiTrackingType} from 'common/src/modules/movie/movieLayers.enums';

// Schema for individual asset (kept for potential future use)
export const assetSchema = z.object({
  id: z.string().describe('Asset identifier'),
  contentDescription: z.string(),
});

// Keyframe schema for animation
export const animatedKeyframeSchema = z.object({
  x: z.number().nullable().optional(),
  y: z.number().nullable().optional(),
  z: z.number().nullable().optional(),
  time: z.number().describe('Time in seconds for this keyframe'),
  easeFromPrevious: z.boolean().nullable().optional().describe('Ease at the end of the previous segment'),
  easeToNext: z.boolean().nullable().optional().describe('Ease at the start of the next segment'),
});

// Schema for transform animation parameters (without layerId)
export const transformAnimationSchema = z.object({
  position: z.array(animatedKeyframeSchema).nullable().optional().describe('Array of position keyframes'),
  anchor: z.array(animatedKeyframeSchema).nullable().optional().describe('Array of anchor keyframes'),
  scale: z
    .array(animatedKeyframeSchema)
    .nullable().optional()
    .describe('Array of scale keyframes (100 = normal size)'),
  rotationX: z
    .array(animatedKeyframeSchema)
    .nullable().optional()
    .describe('Array of X rotation keyframes. 1D values (degrees)'),
  rotationY: z
    .array(animatedKeyframeSchema)
    .nullable().optional()
    .describe(
      'Array of Y rotation keyframes. 1D values, so use must use the x value in keyframes (degrees)',
    ),
  rotationZ: z
    .array(animatedKeyframeSchema)
    .nullable().optional()
    .describe(
      'Array of Z rotation keyframes. 1D values, so use must use the x value in keyframes (clockwise positive degrees)',
    ),
  opacity: z
    .array(animatedKeyframeSchema)
    .nullable().optional()
    .describe(
      'Array of opacity keyframes (1D values, so use must use the x value in keyframes, 0-100)',
    ),
});

// Text layer schemas
export const textPartSchema = z.object({
  text: z.string(),
  color: z.string().nullable().optional().describe('Hex color code (e.g #000000)'),
  size: z.number().nullable().optional(),
  family: z.string().nullable().optional(),
  letterSpacing: z.number().nullable().optional(),
  strikeThrough: z.number().nullable().optional(),
  x_shift: z.number().nullable().optional().describe('x offset in px (positive moves right)'),
  y_shift: z.number().nullable().optional().describe('y offset in px (positive moves down)'),
  url: z.string().nullable().optional().describe('font url'),
});

export const textLineSchema = z.object({
  parts: z.array(textPartSchema),
  align: z.enum(['left', 'center', 'right', 'original']).nullable().optional(),
  lead: z.number().nullable().optional(),
});

// Shape layer schemas
export const pathPointSchema = z.object({
  x: z.number().describe('vertex X'),
  y: z.number().describe('vertex Y'),
  ix: z.number().nullable().optional().describe('in tangent point X relative to the vertex'),
  iy: z.number().nullable().optional().describe('in tangent point Y relative to the vertex'),
  ox: z.number().nullable().optional().describe('out tangent point X relative to the vertex'),
  oy: z.number().nullable().optional().describe('out tangent point Y relative to the vertex'),
});

export const rectangleShapeSchema = z.object({
  width: z.number().describe('Rectangle width'),
  height: z.number().describe('Rectangle height'),
  cornerRadius: z.number().nullable().optional().describe('Corner radius in pixels'),
  x: z.number().nullable().optional().describe('X position relative to layer transform position'),
  y: z.number().nullable().optional().describe('Y position relative to layer transform position'),
});

export const ellipseShapeSchema = z.object({
  width: z.number().describe('Ellipse width'),
  height: z.number().describe('Ellipse height'),
});

export const polygonShapeSchema = z.object({
  sides: z.number().describe('Number of sides'),
  outerRadius: z.number().describe('Outer radius of the polygon'),
  innerRadius: z.number().nullable().optional().describe('Inner radius for star shape'),
});

export const pathShapeSchema = z.object({
  points: z.array(pathPointSchema).describe('Array of points defining the path'),
  isClosed: z.boolean().describe('Whether the path should be closed'),
});

export const shapePropertiesSchema = z.object({
  fillColor: z.string().nullable().optional().describe('Fill color in hex format (e.g. #FF0000)'),
  strokeColor: z.string().nullable().optional().describe('Stroke color in hex format (e.g. #000000)'),
  strokeWidth: z.number().nullable().optional().describe('Stroke width in pixels'),
  strokeAlign: z.nativeEnum(MovieStrokeType).nullable().optional().describe('Stroke alignment'),
  opacity: z.number().nullable().optional().describe('Shape opacity (0-100)'),
});

export const shapeSchema = z.object({
  type: z.enum(['rectangle', 'ellipse', 'polygon', 'path']).describe('Type of shape to create'),
  properties: shapePropertiesSchema.describe('Shape styling properties'),

  // Shape-specific geometry (conditionally required based on type)
  rectangle: rectangleShapeSchema.nullable().optional(),
  ellipse: ellipseShapeSchema.nullable().optional(),
  polygon: polygonShapeSchema.nullable().optional(),
  path: pathShapeSchema.nullable().optional(),
});

// Text animator schema for LLM tool calls
export const textAnimatorSchema = z.object({
  opacity: z
    .array(animatedKeyframeSchema)
    .nullable().optional()
    .describe('opacity keyframes (use x for value, 0-100)'),
  color: z
    .array(animatedKeyframeSchema)
    .nullable().optional()
    .describe('Array of color keyframes (x/y/z = r/g/b, values 0-255)'),
  position: z.array(animatedKeyframeSchema).nullable().optional().describe('Array of position keyframes'),
  tracking: z
    .object({
      amount: z
        .array(animatedKeyframeSchema)
        .describe('Array of tracking amount keyframes (use x for value)'),
      type: z.nativeEnum(AiTrackingType).describe('Type of tracking animation'),
    })
    .nullable().optional(),
  rangeSelector: z.object({
    start: z.array(animatedKeyframeSchema).describe('range start (keyframe, use x for value)'),
    end: z.array(animatedKeyframeSchema).describe('range end (keyframe, use x for value)'),
    offset: z.array(animatedKeyframeSchema).describe('Array of offset keyframes (use x for value)'),
    unit: z.literal('Percent').describe('Unit type for range values (percentage)'),
    smoothness: z.number().min(0).max(100).describe('Smoothness value (0-100)'),
    basedOn: z
      .nativeEnum(AiAnimatorRangeBase)
      .default(AiAnimatorRangeBase.Char)
      .describe('What to animate'),
  }),
});
